import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import MaintenancePage from './components/MaintenancePage';
import PublicOnly from './components/PublicOnly';
import RequireAuth from './components/RequireAuth';
import SplashScreen from './components/SplashScreen';
import { CONFIG } from './config';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Lazy load pages
const Landing = lazy(() => import('./pages/Landing'));

import SignUp from './pages/SignUp';

const Login = lazy(() => import('./pages/Login'));
const Billing = lazy(() => import('./pages/Billing'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const Account = lazy(() => import('./pages/Account'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Security = lazy(() => import('./pages/Security'));
const WhatWeStore = lazy(() => import('./pages/WhatWeStore'));
const Docs = lazy(() => import('./pages/Docs'));

const queryClient = new QueryClient();

function AppContent() {
    const { isLoading } = useAuth();

    if (isLoading) {
        return <SplashScreen />;
    }

    if (CONFIG.MAINTENANCE_MODE) {
        return <MaintenancePage />;
    }

    return (
        <Suspense fallback={<SplashScreen />}>
            <Routes>
                {/* Public Only Routes */}
                <Route element={<PublicOnly />}>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                </Route>

                {/* Protected Routes */}
                <Route element={<RequireAuth />}>
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    <Route path="/billing/return" element={<BillingSuccess />} />
                    <Route path="/account" element={<Account />} />
                </Route>

                {/* Legacy redirects */}
                <Route path="/settings" element={<Navigate to="/account" replace />} />
                <Route path="/dashboard" element={<Navigate to="/account" replace />} />

                {/* Universal Routes */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/security" element={<Security />} />
                <Route path="/what-we-store" element={<WhatWeStore />} />
                <Route path="/docs" element={<Docs />} />

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
                <HelmetProvider>
                    <AuthProvider>
                        <BrowserRouter>
                            <AppContent />
                        </BrowserRouter>
                    </AuthProvider>
                </HelmetProvider>
            </ErrorBoundary>
        </QueryClientProvider>
    );
}
